import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, CheckCircle, Users, Award, TrendingUp, Star, Clock, Shield, Zap, Globe, Target, BarChart3, Phone, Mail, MapPin, Play, ChevronRight, ChevronLeft, Monitor, Smartphone, Cloud, Settings, Palette, Megaphone, Lightbulb, Headphones, Rocket, Briefcase, Heart, Eye, Code, Database, Server, Lock, BarChart, PieChart, Users2, Calendar, MessageSquare, FileText, Video, Camera, Music, Coffee, Target as TargetIcon, GraduationCap, Factory, Home } from 'lucide-react';
import AnimatedSection from '../components/AnimatedSection';
import AnimatedCounter from '../components/AnimatedCounter';
import LottieAnimation from '../components/LottieAnimation';
import NewsletterSignup from '../components/NewsletterSignup';
import { Helmet } from 'react-helmet-async';
import WorldConnectionsMap from '../components/WorldConnectionsMap';

// Import animation data
import heroAnimation from '../assets/animations/hero-animation.json';
import loadingAnimation from '../assets/animations/loading-animation.json';

/**
 * HomePage component - Main landing page for VB Entreprise
 * Author: VB Entreprise
 * 
 * Features:
 * - Professional hero section with Jordy Blue theme
 * - Service category overviews
 * - Client testimonials and success metrics
 * - Process workflow section
 * - Technology stack showcase
 * - Enhanced CTA sections
 * - Contact information section
 * - Additional engaging content sections
 * - Premium animations and interactions
 */
const HomePage: React.FC = () => {
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);

  // Service categories overview
  const serviceCategories = [
    {
      title: 'Advertising & Marketing',
      description: 'Comprehensive digital marketing strategies that drive growth and maximize ROI.',
      icon: Megaphone,
      color: 'from-jordy-blue-500 to-jordy-blue-600',
      features: ['SEO & PPC', 'Social Media', 'Content Marketing', 'Brand Management']
    },
    {
      title: 'IT & Software Development',
      description: 'Custom software solutions, web applications, and mobile apps built with cutting-edge technologies.',
      icon: Monitor,
      color: 'from-jordy-blue-600 to-jordy-blue-700',
      features: ['Web Applications', 'Mobile Apps', 'Cloud Solutions', 'API Development']
    },
    {
      title: 'Consulting & Strategy',
      description: 'Strategic guidance for digital transformation and business growth.',
      icon: Lightbulb,
      color: 'from-jordy-blue-400 to-jordy-blue-500',
      features: ['Digital Transformation', 'Strategy Planning', 'Technology Advisory', 'Innovation Workshops']
    },
    {
      title: 'Design & Creative',
      description: 'Professional design services that bring your brand to life.',
      icon: Palette,
      color: 'from-jordy-blue-300 to-jordy-blue-400',
      features: ['UI/UX Design', 'Brand Identity', 'Graphic Design', 'Video Production']
    },
    {
      title: 'Managed IT & Support',
      description: '24/7 IT support and infrastructure management to keep your business running smoothly.',
      icon: Headphones,
      color: 'from-jordy-blue-700 to-jordy-blue-800',
      features: ['24/7 Support', 'Cloud Management', 'Security', 'DevOps']
    }
  ];

  // Testimonials data
  const testimonials = [
    {
      name: 'CEO, Fintech Company',
      company: '',
      text: 'VB Entreprise transformed our digital presence. Their comprehensive approach to marketing and development delivered exceptional results that exceeded our expectations.',
      rating: 5,
      avatar: 'https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=150'
    },
    {
      name: 'CTO, SaaS Provider',
      company: '',
      text: 'Outstanding technical expertise and project management. They helped us scale our platform to handle 10x the traffic while maintaining security and performance.',
      rating: 5,
      avatar: 'https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=150'
    },
    {
      name: 'CMO, E-commerce Brand',
      company: '',
      text: 'Their digital marketing strategies increased our lead generation by 300%. The combination of SEO, PPC, and content marketing was game-changing.',
      rating: 5,
      avatar: 'https://images.pexels.com/photos/3777943/pexels-photo-3777943.jpeg?auto=compress&cs=tinysrgb&w=150'
    }
  ];

  // Process steps
  const processSteps = [
    {
      step: '01',
      title: 'Discovery & Strategy',
      description: 'We analyze your business needs and create a comprehensive digital strategy.',
      icon: Target
    },
    {
      step: '02',
      title: 'Design & Planning',
      description: 'Our experts design solutions that align with your brand and objectives.',
      icon: BarChart3
    },
    {
      step: '03',
      title: 'Development & Testing',
      description: 'We build and rigorously test your solution to ensure quality and performance.',
      icon: Zap
    },
    {
      step: '04',
      title: 'Launch & Support',
      description: 'We launch your solution and provide ongoing support and optimization.',
      icon: Globe
    }
  ];

  // Technology stack
  const technologies = [
    { name: 'React', category: 'Frontend' },
    { name: 'Node.js', category: 'Backend' },
    { name: 'Python', category: 'Backend' },
    { name: 'AWS', category: 'Cloud' },
    { name: 'Docker', category: 'DevOps' },
    { name: 'MongoDB', category: 'Database' },
    { name: 'TypeScript', category: 'Frontend' },
    { name: 'Kubernetes', category: 'DevOps' },
    { name: 'Laravel', category: 'Backend' },
    { name: 'Flutter', category: 'Mobile' },
    { name: 'Shopify', category: 'E-commerce' },
    { name: 'Google Ads', category: 'Marketing' }
  ];

  // Why choose us features
  const whyChooseUs = [
    {
      icon: Rocket,
      title: 'Innovation First',
      description: 'We stay ahead of the curve with cutting-edge technologies and creative solutions.'
    },
    {
      icon: Shield,
      title: 'Security & Reliability',
      description: 'Your data and systems are protected with enterprise-grade security measures.'
    },
    {
      icon: Users2,
      title: 'Expert Team',
      description: 'Our experienced professionals bring diverse expertise to every project.'
    },
    {
      icon: Clock,
      title: 'Fast Delivery',
      description: 'We deliver high-quality solutions on time, every time.'
    },
    {
      icon: Heart,
      title: 'Client Focused',
      description: 'Your success is our priority. We build lasting partnerships.'
    },
    {
      icon: BarChart,
      title: 'Proven Results',
      description: 'Track record of delivering measurable business impact and ROI.'
    }
  ];

  // Industry solutions
  const industrySolutions = [
    {
      title: 'Healthcare',
      description: 'HIPAA-compliant solutions for modern healthcare providers.',
      icon: Heart,
      color: 'from-red-500 to-pink-500'
    },
    {
      title: 'Finance',
      description: 'Secure financial applications with regulatory compliance.',
      icon: BarChart,
      color: 'from-green-500 to-emerald-500'
    },
    {
      title: 'E-commerce',
      description: 'Scalable online stores and marketplace solutions.',
      icon: TargetIcon,
      color: 'from-purple-500 to-indigo-500'
    },
    {
      title: 'Education',
      description: 'Digital learning platforms and educational technology.',
      icon: GraduationCap,
      color: 'from-blue-500 to-cyan-500'
    },
    {
      title: 'Manufacturing',
      description: 'IoT and automation solutions for smart manufacturing.',
      icon: Factory,
      color: 'from-orange-500 to-red-500'
    },
    {
      title: 'Real Estate',
      description: 'Property management and real estate technology platforms.',
      icon: Home,
      color: 'from-teal-500 to-green-500'
    }
  ];

  // Success metrics
  const successMetrics = [
    { number: 500, label: 'Projects Completed', icon: Briefcase, suffix: '+' },
    { number: 98, label: 'Client Satisfaction', icon: Star, suffix: '%' },
    { number: 24, label: 'Support Available', icon: Clock, suffix: '/7' },
    { number: 50, label: 'Team Experts', icon: Users, suffix: '+' },
    { number: 10, label: 'Years Experience', icon: Award, suffix: '+' },
    { number: 300, label: 'Average ROI', icon: TrendingUp, suffix: '%' }
  ];

  return (
    <>
      <Helmet>
        <title>VB Entreprise | Premium IT & Digital Marketing Agency for US, UK & Canada Businesses</title>
      </Helmet>
      <div className="overflow-hidden bg-white text-gray-900">
        {/* Hero Section */}
        <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-jordy-blue-50 via-white to-jordy-blue-100 overflow-hidden">
          <motion.div 
            className="absolute inset-0 bg-[url('https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&cs=tinysrgb&w=1920')] bg-cover bg-center opacity-10"
            style={{ y }}
          />
          
          {/* Floating Animation Elements */}
          <motion.div
            className="absolute top-20 left-20 w-32 h-32 bg-jordy-blue-200 rounded-full opacity-20"
            animate={{
              y: [0, -20, 0],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <motion.div
            className="absolute bottom-20 right-20 w-24 h-24 bg-jordy-blue-300 rounded-full opacity-20"
            animate={{
              y: [0, 20, 0],
              scale: [1, 0.9, 1],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="space-y-8">
              <AnimatedSection delay={0.2}>
                <div className="space-y-6">
                  <motion.div 
                    className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-jordy-blue-500 to-jordy-blue-600 text-white rounded-full text-sm font-medium shadow-lg"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Award className="w-4 h-4 mr-2" />
                    Award-Winning Digital Solutions
                  </motion.div>
                  
                  <motion.h1 
                    className="text-5xl lg:text-7xl font-bold leading-tight"
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                  >
                    <span className="bg-gradient-to-r from-jordy-blue-600 to-jordy-blue-800 bg-clip-text text-transparent">
                      Transform
                    </span>
                    <br />
                    <span className="text-gray-900">Your Business</span>
                    <br />
                    <span className="bg-gradient-to-r from-jordy-blue-600 to-jordy-blue-800 bg-clip-text text-transparent">
                      With Excellence
                    </span>
                  </motion.h1>
                  
                  <motion.p 
                    className="text-xl lg:text-2xl text-gray-600 leading-relaxed max-w-4xl mx-auto"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.5 }}
                  >
                    VB Entreprise delivers comprehensive digital solutions including advertising, marketing, IT development, consulting, design, and managed services to drive growth and innovation.
                  </motion.p>
                </div>
              </AnimatedSection>

              <AnimatedSection delay={0.7}>
                <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-6">
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Link
                      to="/contact"
                      className="group bg-gradient-to-r from-jordy-blue-500 to-jordy-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:from-jordy-blue-600 hover:to-jordy-blue-700 transition-all duration-300 shadow-xl text-center inline-flex items-center"
                    >
                      Get Free Consultation
                      <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </motion.div>
                </div>
              </AnimatedSection>

              <AnimatedSection delay={1.0}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pt-12">
                  {[
                    { number: 500, label: 'Projects Delivered', suffix: '+' },
                    { number: 98, label: 'Client Satisfaction', suffix: '%' },
                    { number: 24, label: 'Support Available', suffix: '/7' }
                  ].map((metric, index) => (
                    <motion.div
                      key={index}
                      className="text-center p-6 bg-white/80 backdrop-blur-sm rounded-xl border border-jordy-blue-200 shadow-lg"
                      whileHover={{ y: -5, scale: 1.02 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="text-3xl font-bold text-jordy-blue-600">
                        <AnimatedCounter end={metric.number} suffix={metric.suffix} />
                      </div>
                      <div className="text-gray-600">{metric.label}</div>
                    </motion.div>
                  ))}
                </div>
              </AnimatedSection>
            </div>
          </div>

          {/* Hero Animation */}
          <motion.div
            className="absolute bottom-10 right-10 w-32 h-32 opacity-30"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 0.3, scale: 1 }}
            transition={{ duration: 1, delay: 1.5 }}
          >
            <LottieAnimation
              animationData={heroAnimation}
              className="w-full h-full"
            />
          </motion.div>
        </section>

        {/* Service Categories Overview */}
        <AnimatedSection>
          <section className="py-20 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-16">
                <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Our Service Categories</h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  From advertising and marketing to IT development and consulting, we provide end-to-end digital solutions to accelerate your business growth.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {serviceCategories.map((category, index) => (
                  <motion.div
                    key={index}
                    className="group bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 border border-gray-200"
                    whileHover={{ y: -10, scale: 1.02 }}
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <motion.div
                      className={`w-16 h-16 bg-gradient-to-r ${category.color} rounded-xl flex items-center justify-center mb-6`}
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      transition={{ duration: 0.3 }}
                    >
                      <category.icon className="w-8 h-8 text-white" />
                    </motion.div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">{category.title}</h3>
                    <p className="text-gray-600 mb-6">{category.description}</p>
                    <div className="grid grid-cols-2 gap-2 mb-6">
                      {category.features.map((feature, idx) => (
                        <motion.div
                          key={idx}
                          className="flex items-center space-x-2 text-sm text-gray-600"
                          whileHover={{ x: 5 }}
                          transition={{ duration: 0.2 }}
                        >
                          <CheckCircle size={14} className="text-jordy-blue-500 flex-shrink-0" />
                          <span>{feature}</span>
                        </motion.div>
                      ))}
                    </div>
                    <Link
                      to="/services"
                      className="inline-flex items-center text-jordy-blue-600 font-medium hover:text-jordy-blue-700 transition-colors group/link"
                    >
                      Learn More <ArrowRight size={16} className="ml-1 group-hover/link:translate-x-1 transition-transform" />
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        </AnimatedSection>

        {/* Why Choose Us */}
        <AnimatedSection>
          <section className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-16">
                <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Why Choose VB Entreprise?</h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  We combine technical expertise with creative innovation to deliver exceptional results that drive your business forward.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {whyChooseUs.map((feature, index) => (
                  <motion.div
                    key={index}
                    className="text-center p-8 bg-gradient-to-br from-jordy-blue-50 to-white rounded-2xl border border-jordy-blue-200 hover:border-jordy-blue-300 transition-colors"
                    whileHover={{ y: -5, scale: 1.02 }}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <motion.div
                      className="w-16 h-16 bg-gradient-to-r from-jordy-blue-500 to-jordy-blue-600 rounded-full flex items-center justify-center mx-auto mb-6"
                      whileHover={{ scale: 1.1, rotate: 360 }}
                      transition={{ duration: 0.5 }}
                    >
                      <feature.icon className="w-8 h-8 text-white" />
                    </motion.div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        </AnimatedSection>

        {/* Success Metrics */}
        <AnimatedSection>
          <section className="py-20 bg-gradient-to-r from-jordy-blue-500 to-jordy-blue-600">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-16">
                <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">Our Success Metrics</h2>
                <p className="text-xl text-jordy-blue-100 max-w-3xl mx-auto">
                  Numbers that speak for themselves - our track record of delivering exceptional results.
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
                {successMetrics.map((metric, index) => (
                  <motion.div
                    key={index}
                    className="text-center"
                    whileHover={{ scale: 1.05 }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <motion.div
                      className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4"
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      transition={{ duration: 0.3 }}
                    >
                      <metric.icon className="w-8 h-8 text-white" />
                    </motion.div>
                    <div className="text-3xl font-bold text-white mb-2">
                      <AnimatedCounter end={metric.number} suffix={metric.suffix} />
                    </div>
                    <div className="text-jordy-blue-100 text-sm">{metric.label}</div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        </AnimatedSection>

        {/* Process Section */}
        <AnimatedSection>
          <section className="py-20 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-16">
                <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Our Process</h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  A proven methodology that ensures successful project delivery and exceptional results across all our service categories.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {processSteps.map((step, index) => (
                  <motion.div
                    key={index}
                    className="relative"
                    whileHover={{ y: -5 }}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.2 }}
                  >
                    <div className="bg-white rounded-2xl p-8 border border-gray-200 hover:border-jordy-blue-300 transition-colors shadow-lg">
                      <motion.div
                        className="text-4xl font-bold text-jordy-blue-600 mb-4"
                        whileHover={{ scale: 1.1 }}
                      >
                        {step.step}
                      </motion.div>
                      <motion.div
                        className="w-12 h-12 bg-gradient-to-r from-jordy-blue-500 to-jordy-blue-600 rounded-lg flex items-center justify-center mb-6"
                        whileHover={{ scale: 1.1, rotate: 5 }}
                      >
                        <step.icon className="w-6 h-6 text-white" />
                      </motion.div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">{step.title}</h3>
                      <p className="text-gray-600">{step.description}</p>
                    </div>
                    {index < processSteps.length - 1 && (
                      <motion.div
                        className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2"
                        animate={{ x: [0, 5, 0] }}
                        transition={{ duration: 2, repeat: Infinity }}
                      >
                        <ChevronRight className="w-8 h-8 text-jordy-blue-400" />
                      </motion.div>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        </AnimatedSection>

        {/* Industry Solutions */}
        <AnimatedSection>
          <section className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-16">
                <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Industry Solutions</h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Tailored solutions designed specifically for your industry's unique challenges and requirements.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {industrySolutions.map((industry, index) => (
                  <motion.div
                    key={index}
                    className="group bg-gray-50 rounded-2xl p-8 hover:bg-white hover:shadow-lg transition-all duration-300 border border-gray-200 hover:border-jordy-blue-200"
                    whileHover={{ y: -8, scale: 1.02 }}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <motion.div
                      className={`w-16 h-16 bg-gradient-to-r ${industry.color} rounded-xl flex items-center justify-center mb-6`}
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      transition={{ duration: 0.3 }}
                    >
                      <industry.icon className="w-8 h-8 text-white" />
                    </motion.div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">{industry.title}</h3>
                    <p className="text-gray-600">{industry.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        </AnimatedSection>

        {/* Technology Stack */}
        <AnimatedSection>
          <section className="py-20 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-16">
                <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Technology & Tools</h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  We leverage cutting-edge technologies and platforms to build robust, scalable, and innovative solutions.
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {technologies.map((tech, index) => (
                  <motion.div
                    key={index}
                    className="bg-white rounded-xl p-6 text-center hover:bg-jordy-blue-50 transition-colors border border-gray-200 hover:border-jordy-blue-200 shadow-md"
                    whileHover={{ y: -5, scale: 1.05 }}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <div className="text-gray-900 font-semibold text-lg mb-2">{tech.name}</div>
                    <div className="text-jordy-blue-600 text-sm font-medium">{tech.category}</div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        </AnimatedSection>

        {/* Testimonials */}
        <AnimatedSection>
          <section className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-16">
                <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">What Our Clients Say</h2>
                <p className="text-xl text-gray-600">
                  Don't just take our word for it - hear from our satisfied clients across different industries.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {testimonials.map((testimonial, index) => (
                  <motion.div
                    key={index}
                    className="bg-gradient-to-br from-jordy-blue-50 to-white rounded-2xl p-8 border border-jordy-blue-200 hover:border-jordy-blue-300 transition-colors shadow-lg"
                    whileHover={{ y: -5, scale: 1.02 }}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.2 }}
                  >
                    <div className="flex items-center mb-6">
                      <motion.img
                        src={testimonial.avatar}
                        alt={testimonial.name}
                        className="w-12 h-12 rounded-full object-cover mr-4 border-2 border-jordy-blue-200"
                        whileHover={{ scale: 1.1 }}
                      />
                      <div>
                        <div className="font-semibold text-gray-900">{testimonial.name}</div>
                        <div className="text-jordy-blue-600 text-sm font-medium">{testimonial.company}</div>
                      </div>
                    </div>
                    <div className="flex items-center mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, scale: 0 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: index * 0.2 + i * 0.1 }}
                        >
                          <Star size={16} className="text-jordy-blue-500 fill-current" />
                        </motion.div>
                      ))}
                    </div>
                    <p className="text-gray-700 italic">"{testimonial.text}"</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        </AnimatedSection>

        {/* Newsletter Signup Section */}
        <AnimatedSection>
          <section className="py-20 bg-white">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Stay Updated & Get Free Resources</h2>
                <p className="text-lg text-gray-600">Join our newsletter for exclusive insights, industry trends, and valuable resources to help grow your business.</p>
              </div>
              <NewsletterSignup />
            </div>
          </section>
        </AnimatedSection>

        {/* Enhanced CTA Section */}
        <AnimatedSection>
          <section className="py-20 bg-gradient-to-r from-jordy-blue-500 to-jordy-blue-600">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
                Ready to Transform Your Business?
              </h2>
              <p className="text-xl text-jordy-blue-100 mb-8 max-w-2xl mx-auto">
                Get started with a free consultation and discover how VB Entreprise can help you achieve your digital goals across all our service categories.
              </p>
              <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-6">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to="/contact"
                    className="bg-white text-jordy-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center shadow-lg"
                  >
                    <Phone className="mr-2 w-5 h-5" />
                    Get Free Consultation
                  </Link>
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to="/services"
                    className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-jordy-blue-600 transition-colors inline-flex items-center"
                  >
                    <ArrowRight className="mr-2 w-5 h-5" />
                    Explore All Services
                  </Link>
                </motion.div>
              </div>
            </div>
          </section>
        </AnimatedSection>

        {/* Contact Information */}
        <AnimatedSection>
          <section className="py-20 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Get In Touch</h2>
                <p className="text-lg text-gray-600">Ready to start your project? Contact us today.</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[
                  { icon: Phone, title: 'Call Us', info: '9909206443' },
                  { icon: Mail, title: 'Email Us', info: 'info@vbentreprise.com' }
                ].map((contact, index) => (
                  <motion.div
                    key={index}
                    className="text-center"
                    whileHover={{ y: -5 }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <motion.div
                      className="w-16 h-16 bg-gradient-to-r from-jordy-blue-500 to-jordy-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg"
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      transition={{ duration: 0.3 }}
                    >
                      <contact.icon className="w-8 h-8 text-white" />
                    </motion.div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{contact.title}</h3>
                    <p className="text-gray-600 whitespace-pre-line">{contact.info}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        </AnimatedSection>

        {/* Loading Animation Overlay */}
        <motion.div
          className="fixed top-4 right-4 w-16 h-16 z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
        >
          <LottieAnimation
            animationData={loadingAnimation}
            className="w-full h-full"
          />
        </motion.div>
      </div>
    </>
  );
};

export default HomePage;